import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { useTrackMarketplaceEvent } from 'marketplace-ui-tracking';
import { getMarketplaceInteractionEvent } from 'marketplace-ui-tracking/events/marketplace';
import { useCallback, useMemo } from 'react';
import { useProductTypeSearchParam } from '../../hooks/useProductTypeSearchParams';
import { useAppliedFilters } from './useAppliedFilters';
/**
 * If you apply a product-specific filter without first applying the product type filter,
 * we'll automatically apply the product type filter for you.
 *
 * If you then remove that product-specific filter, we'll clean up after ourselves and
 * remove the product type filter.
 *
 * However, we don't want to remove the product type filter if the user has manually
 * applied it, so this flag helps with that.
 */
let productTypeFilterWasAutoApplied = false;
export function useFilterChangeHandler({
  filter,
  isMulti,
  isToggle
}) {
  const trackMarketplaceEvent = useTrackMarketplaceEvent();
  const appliedFilters = useAppliedFilters();
  const params = useSearchParams();
  const productType = useProductTypeSearchParam();
  const defaultValue = useMemo(() => {
    if (filter !== null && filter !== void 0 && filter.defaultValue) {
      return isToggle && filter.defaultValue === 'all' ? 'false' : filter.defaultValue;
    }
    return isMulti && [] || isToggle && 'false' || '';
  }, [filter, isMulti, isToggle]);
  return useCallback(e => {
    if (!filter) return;
    const value = isToggle ? e.target.checked ? 'true' : 'false' : e.target.value;
    if (filter.productType && !productType) {
      params.set('PRODUCT_TYPE', filter.productType);
      productTypeFilterWasAutoApplied = true;
    }
    const thisIsTheOnlyProductSpecificFilter = filter.productType && appliedFilters.filter(f => f.productType).length === 1;
    if (value === defaultValue && thisIsTheOnlyProductSpecificFilter && productTypeFilterWasAutoApplied) {
      params.delete('PRODUCT_TYPE');
      productTypeFilterWasAutoApplied = false;
    }
    if (isMulti) {
      params.setList(filter.field, value !== null && value !== void 0 ? value : []);
    } else {
      params.set(filter.field, value, {
        defaultValue: defaultValue
      });
    }

    // reset page number, since now we're showing a different set of results
    params.delete('page');
    trackMarketplaceEvent(getMarketplaceInteractionEvent({
      action: 'filter changed',
      queryType: params.get('queryType'),
      search: params.get('search'),
      filterType: filter.field,
      filterValue: [value]
    }));
  }, [appliedFilters, defaultValue, filter, isMulti, isToggle, params, productType, trackMarketplaceEvent]);
}